/**
 * Phase 32 Plan 32-3 — value-tag form modal (UI-SPEC §175-181).
 *
 * Fields (from UI-SPEC):
 *   - Engineer combobox (sourced from useAdminUsers, soft-deleted filtered out)
 *   - Project combobox (sourced from useProjects)
 *   - From date <DateField required>
 *   - To date <DateField required>
 *   - Category select (8-value enum, Title Case labels per UI-SPEC §180)
 *   - Note <Textarea>
 *
 * Inline validation (UI-SPEC §179): toDate >= fromDate, else display
 * "End date must be on or after the start date." and DO NOT call the
 * mutation. The mutation hook also surfaces server-side errors as the
 * UI-SPEC §165 inline banner ("Couldn't save the tag. Check the form and
 * try again.").
 *
 * Submit copy (UI-SPEC §154): "Save tag" (create) / "Update tag" (edit).
 */
import { useState, useEffect, type ReactElement } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Textarea } from '../common/Input';
import { useProjects } from '../../hooks/useProjects';
import { useAdminUsers } from '../../hooks/useAdmin';
import {
  useCreateValueTag,
  useUpdateValueTag,
} from '../../hooks/useValueTags';
import type {
  ValueTag,
  ValueTagCategory,
} from '../../services/api/valueTags';
import { DateField } from '../ui/DateField';

/** The input classes `<Input>` applies, so the two date fields match the row. */
const VALUE_TAG_DATE_INPUT_CLASS =
  'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0';

const CATEGORIES: { value: ValueTagCategory; label: string }[] = [
  { value: 'feature', label: 'Feature' },
  { value: 'bugfix', label: 'Bug fix' },
  { value: 'refactor', label: 'Refactor' },
  { value: 'infra', label: 'Infrastructure' },
  { value: 'docs', label: 'Documentation' },
  { value: 'customer_request', label: 'Customer request' },
  { value: 'tech_debt', label: 'Tech debt' },
  { value: 'other', label: 'Other' },
];

export interface ValueTagFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Edit mode if provided. */
  tag?: ValueTag | null;
  /** Pre-fills the engineer combobox. The drill-down page passes the page's userId. */
  defaultUserId?: number;
  /** Pre-fills the project combobox (optional). */
  defaultProjectId?: number;
}

interface FormState {
  userId: string;
  projectId: string;
  fromDate: string;
  toDate: string;
  category: ValueTagCategory | '';
  note: string;
}

const EMPTY_FORM: FormState = {
  userId: '',
  projectId: '',
  fromDate: '',
  toDate: '',
  category: 'feature',
  note: '',
};

export default function ValueTagFormModal({
  isOpen,
  onClose,
  tag = null,
  defaultUserId,
  defaultProjectId,
}: ValueTagFormModalProps): ReactElement | null {
  const isEdit = !!tag;
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const usersQ = useAdminUsers(false); // includeDeleted=false
  const projectsQ = useProjects();

  const createMut = useCreateValueTag();
  const updateMut = useUpdateValueTag();

  useEffect(() => {
    if (!isOpen) return;
    if (tag) {
      setForm({
        userId: String(tag.userId),
        projectId: String(tag.projectId),
        fromDate: tag.fromDate,
        toDate: tag.toDate,
        category: tag.category,
        note: tag.note ?? '',
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        userId: defaultUserId ? String(defaultUserId) : '',
        projectId: defaultProjectId ? String(defaultProjectId) : '',
      });
    }
    setValidationError(null);
    setSubmitError(null);
  }, [isOpen, tag, defaultUserId, defaultProjectId]);

  if (!isOpen) return null;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const userId = Number(form.userId);
    const projectId = Number(form.projectId);
    if (!Number.isFinite(userId) || userId <= 0) {
      setValidationError('Engineer is required.');
      return;
    }
    if (!Number.isFinite(projectId) || projectId <= 0) {
      setValidationError('Project is required.');
      return;
    }
    if (!form.fromDate || !form.toDate) {
      setValidationError('Both dates are required.');
      return;
    }
    if (form.toDate < form.fromDate) {
      setValidationError('End date must be on or after the start date.');
      return;
    }
    if (!form.category) {
      setValidationError('Category is required.');
      return;
    }

    const note = form.note.trim() === '' ? null : form.note;

    try {
      if (isEdit && tag) {
        await updateMut.mutateAsync({
          id: tag.id,
          input: {
            fromDate: form.fromDate,
            toDate: form.toDate,
            category: form.category,
            note,
          },
        });
      } else {
        await createMut.mutateAsync({
          projectId,
          input: {
            userId,
            fromDate: form.fromDate,
            toDate: form.toDate,
            category: form.category,
            note,
          },
        });
      }
      onClose();
    } catch (err) {
      // UI-SPEC §165 inline banner copy.
      setSubmitError(
        "Couldn't save the tag. Check the form and try again." +
          (err instanceof Error && err.message ? ` (${err.message})` : ''),
      );
    }
  };

  const isSubmitting = createMut.isPending || updateMut.isPending;
  const users = usersQ.data ?? [];
  const projects = projectsQ.data ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit value tag' : 'Add value tag'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Engineer combobox (UI-SPEC §175) */}
        <div>
          <label
            htmlFor="value-tag-engineer"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Engineer<span className="text-red-500 ml-1">*</span>
          </label>
          <select
            id="value-tag-engineer"
            value={form.userId}
            onChange={(e) => set('userId', e.target.value)}
            disabled={isEdit}
            required
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select an engineer…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {[u.lastName, u.firstName].filter(Boolean).join(' ') || u.email}
              </option>
            ))}
          </select>
        </div>

        {/* Project combobox (UI-SPEC §176) */}
        <div>
          <label
            htmlFor="value-tag-project"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Project<span className="text-red-500 ml-1">*</span>
          </label>
          <select
            id="value-tag-project"
            value={form.projectId}
            onChange={(e) => set('projectId', e.target.value)}
            disabled={isEdit}
            required
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Date span (UI-SPEC §177-179) */}
        <div className="grid grid-cols-2 gap-3">
          {/* MKS-351 — DateField has no `label` prop (it owns a popover, not a
              form row), so these two carry the same label chrome `<Input>`
              renders: the label, the required asterisk and the input classes. */}
          <div className="w-full">
            <label
              htmlFor="value-tag-from"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              From<span className="text-red-500 ml-1">*</span>
            </label>
            <DateField
              id="value-tag-from"
              name="fromDate"
              value={form.fromDate}
              onChange={(nextDate) => set('fromDate', nextDate)}
              required
              className={VALUE_TAG_DATE_INPUT_CLASS}
            />
          </div>
          <div className="w-full">
            <label htmlFor="value-tag-to" className="mb-1 block text-sm font-medium text-gray-700">
              To<span className="text-red-500 ml-1">*</span>
            </label>
            <DateField
              id="value-tag-to"
              name="toDate"
              value={form.toDate}
              onChange={(nextDate) => set('toDate', nextDate)}
              required
              className={VALUE_TAG_DATE_INPUT_CLASS}
            />
          </div>
        </div>

        {/* Category select (UI-SPEC §180) */}
        <div>
          <label
            htmlFor="value-tag-category"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Category<span className="text-red-500 ml-1">*</span>
          </label>
          <select
            id="value-tag-category"
            value={form.category}
            onChange={(e) => set('category', e.target.value as ValueTagCategory)}
            required
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {/* Note (UI-SPEC §181) */}
        <Textarea
          id="value-tag-note"
          name="note"
          label="Note"
          rows={3}
          value={form.note}
          onChange={(e) => set('note', e.target.value)}
          placeholder="What did this person ship of value? (optional)"
          maxLength={4000}
        />

        {validationError && (
          <p className="text-sm text-red-600" role="alert">
            {validationError}
          </p>
        )}
        {submitError && (
          <p
            className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700"
            role="alert"
          >
            {submitError}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            {isEdit ? 'Update tag' : 'Save tag'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { ValueTagFormModal };
